import type { ElementType, ReactNode } from "react";
import {
  useTable,
  useGlobalFilter,
  usePagination,
  useBlockLayout,
  useFilters,
  useSortBy,
} from "react-table";
import { ArrowUpward, ArrowDownward } from "@mui/icons-material";

import TableGlobalFilter from "./TableGlobalFilter";
import TablePagination from "./TablePagination";
import TablePageSize from "./TablePageSize";
import NoDataFound from "../NoDataFound";
import DataRangeDisplay from "../DataRangeDisplay";

type TableRecord = Record<string, any>;
type TableClientProps = {
  columns?: TableRecord[];
  data?: TableRecord[];
  filters?: (_setFilter: any, _filters: any) => ReactNode;
  hiddenColumns?: string[];
  hideSearchLabel?: boolean;
  initialPageSize?: number;
  initialSortedBy?: TableRecord[];
  pagination?: boolean;
  searchPlaceholder?: string;
  showFilter?: boolean;
  showPageSize?: boolean;
  showSearch?: boolean;
  totalRecords?: number;
};

const TableGlobalFilterComponent = TableGlobalFilter as ElementType;
const TablePaginationComponent = TablePagination as ElementType;
const TablePageSizeComponent = TablePageSize as ElementType;
const DataRangeDisplayComponent = DataRangeDisplay as ElementType;

const TableClient = ({
  data = [],
  columns = [],
  showSearch = false,
  searchPlaceholder = "Search..",
  pagination = true,
  showPageSize = false,
  initialPageSize = 10,
  filters,
  showFilter = false,
  hiddenColumns = [],
  initialSortedBy = [],
  hideSearchLabel = false,
  totalRecords,
}: TableClientProps) => {
  const {
    getTableProps,
    headerGroups,
    getTableBodyProps,
    // rows,
    state,
    prepareRow,
    preGlobalFilteredRows,
    setGlobalFilter,
    setFilter,
    // The rest of these things are super handy, too ;)
    page, // Instead of using 'rows', we'll use page,
    // which has only the rows for the active page
    // canPreviousPage,
    // canNextPage,
    // pageOptions,
    pageCount,
    gotoPage,
    // nextPage,
    // previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex: 0,
        pageSize: initialPageSize,
        hiddenColumns,
        sortBy: initialSortedBy,
      },
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    usePagination,
    useBlockLayout
  );

  const handlePageChange = (_event: unknown, value: number) => {
    gotoPage(value - 1);
  };

  const getHederTitle = (column: TableRecord) => {
    if (column.isSorted) {
      return column.isSortedDesc ? `Sorted DESC` : `Sorted ASC`;
    }
    return "";
  };

  return (
    <div className="rp-react-table">
      {showSearch && (
        <div className="row my-2 justify-content-end align-items-center">
          {showFilter && filters && filters(setFilter, state.filters)}
          <div
            className={`${
              showFilter && filters ? "col col-lg-3" : "col-md-4"
            } mb-2`}
          >
            <TableGlobalFilterComponent
              preGlobalFilteredRows={preGlobalFilteredRows}
              globalFilter={state.globalFilter}
              setGlobalFilter={setGlobalFilter}
              placeholder={searchPlaceholder}
              hideSearchLabel={hideSearchLabel}
            />
          </div>
        </div>
      )}
      <div className="table-responsive">
        <table {...getTableProps()} className="table table-borderless">
          <thead>
            {headerGroups.map((headerGroup) => {
              const { key: headerGroupKey, ...headerGroupProps } =
                headerGroup.getHeaderGroupProps();
              return (
                <tr
                  key={headerGroupKey}
                  {...headerGroupProps}
                  className="w-100"
                >
                  {headerGroup.headers.map((column) => {
                    const { key: columnKey, ...columnProps } =
                      column.getHeaderProps(
                        column.getSortByToggleProps({
                          title: getHederTitle(column),
                        })
                      );
                    return (
                      <th key={columnKey} {...columnProps} className="w-100">
                        {column.render("Header")}
                        <div
                          style={{
                            position: "absolute",
                            right: 2,
                            top: 0,
                            bottom: 0,
                            height: "100%",
                            display: "flex",
                            justifyContent: "center",
                            alignContent: "center",
                          }}
                        >
                          {column.canSort ? (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                alignContent: "center",
                                height: "100%",
                              }}
                            >
                              <ArrowUpward
                                style={{ fontSize: 9 }}
                                color={
                                  column.isSorted && !column.isSortedDesc
                                    ? "inherit"
                                    : "disabled"
                                }
                              />
                              <ArrowDownward
                                style={{ fontSize: 9 }}
                                color={
                                  column.isSorted && column.isSortedDesc
                                    ? "inherit"
                                    : "disabled"
                                }
                              />
                            </div>
                          ) : null}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page && page.length ? (
              page.map((row) => {
                prepareRow(row);
                const { key: rowKey, ...rowProps } = row.getRowProps();
                return (
                  <tr key={rowKey} {...rowProps} className="w-100">
                    {row.cells.map((cell) => {
                      const { key: cellKey, ...cellProps } =
                        cell.getCellProps();
                      return (
                        <td key={cellKey} {...cellProps} className="w-100">
                          {cell.render("Cell")}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={42}>
                  <NoDataFound height="auto" />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-1">
        <div className="row align-items-center">
          {showPageSize === true && (
            <div className="col-1">
              <TablePageSizeComponent
                pageSize={pageSize}
                setPageSize={setPageSize}
              />
            </div>
          )}
          <div className="col-lg-6 mb-2 ml-2">
            <DataRangeDisplayComponent
              pageIndex={pageIndex}
              totalRecords={totalRecords}
            />
          </div>
          {pagination === true && pageCount > 0 && (
            <div className="col d-flex justify-content-end">
              <TablePaginationComponent
                count={pageCount}
                page={pageIndex + 1}
                onChange={handlePageChange}
                variant="outlined"
                shape="rounded"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TableClient;
